import { customAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const KEYWORDS =
  "gcp googleapis platform iam service account workspace delegation";
// www.googleapis.com also serves OAuth, so only the Workspace API paths on that host get this credential.
const defaultUrlPattern = () =>
  "https://(?:w*[^w/.][^/]*\\.googleapis\\.com/.*|www\\.googleapis\\.com/(?:(?:(?:upload|download)/)?(?:drive|gmail|groups)|calendar|tasks|webmasters|admin|apps/licensing|batch)/.*)";

const serviceAccountConnectionTest = {
  protocol: "http",
  request: () => ({
    url: "https://cloudresourcemanager.googleapis.com/v1/projects",
    expectedStatus: [200, 403],
  }),
} as const;

const serviceAccountKeyVariant = productVariant({
  identifier: "googleCloud",
  authLabel: "Service account key",
  keywords: KEYWORDS,
  protocol: "http",
  defaultUrlPattern,
  auth: customAuth("googleCloud"),
  steps: [
    {
      title: "Create a service account",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [Google Cloud Console](https://console.cloud.google.com/iam-admin/serviceaccounts) and create a new service account, or use an existing one.\n\nGrant the service account the necessary roles for the APIs you want to use.",
        },
      ],
    },
    {
      title: "Generate a service account key",
      items: [
        {
          kind: "markdown",
          content:
            "In the service account details, go to the **Keys** tab and click **Add Key** → **Create new key**. Choose **JSON** format.\n\nThe key file will be downloaded automatically. Open it and copy the entire JSON contents.",
        },
      ],
    },
    {
      title: "Enter your service account key",
      items: [
        {
          kind: "input",
          label: "Service account key (JSON)",
          placeholder: '{"type": "service_account", "project_id": "...", ...}',
          required: true,
          target: { kind: "secrets", field: "serviceAccountKey" },
          helperText: "Paste the entire JSON key file contents.",
        },
      ],
    },
    {
      title: "Domain-wide delegation",
      items: [
        {
          kind: "select",
          label: "User delegation",
          target: { kind: "configuration", field: "delegationMode" },
          options: [
            { value: "", label: "None" },
            { value: "static", label: "Specific user" },
            { value: "dynamic", label: "Dynamic (set per-request)" },
          ],
          defaultValue: "",
          helperText:
            "Impersonate a Google Workspace user via domain-wide delegation.",
        },
        {
          kind: "input",
          label: "Delegation email",
          placeholder: "user@example.com",
          target: { kind: "configuration", field: "delegationSubject" },
          helperText: "The email of the user to impersonate.",
          visibleWhen: [
            {
              target: { kind: "configuration", field: "delegationMode" },
              value: "static",
            },
          ],
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "https://www.googleapis.com/auth/gmail.readonly",
          target: { kind: "configuration", field: "scope" },
          helperText:
            "Space-separated OAuth scopes. Must match the scopes granted in the Google Workspace Admin Console.",
          visibleWhen: [
            {
              target: { kind: "configuration", field: "delegationMode" },
            },
          ],
        },
      ],
    },
  ],
  connectionTest: serviceAccountConnectionTest,
  usageHint: ({ delegationMode }) => {
    if (delegationMode !== "dynamic") return undefined;
    return "This connector uses domain-wide delegation with dynamic user impersonation. To set which user to act as, include the header x-3b-delegation-subject with their email (e.g. user@company.com).";
  },
  publicEnvVars: ({ delegationMode, delegationSubject, scope }) => {
    const mode = delegationMode;
    if (!mode) return {} as Record<string, string>;
    const vars: Record<string, string> = {};
    if (mode === "static" && delegationSubject) {
      vars.GCP_DELEGATION_SUBJECT = delegationSubject;
    }
    if (scope) {
      vars.GCP_DELEGATION_SCOPE = scope;
    }
    return vars;
  },
});

const federationVariant = productVariant({
  identifier: "googleCloudFederation",
  authLabel: "Workload identity federation (keyless)",
  keywords: KEYWORDS,
  protocol: "http",
  defaultUrlPattern,
  auth: (app) =>
    customAuth("googleCloudFederation", {
      issuer: app.issuer ?? "",
      projectNumber: app.config.projectNumber,
      poolId: app.config.poolId,
      providerId: app.config.providerId,
      serviceAccountEmail: app.config.serviceAccountEmail,
      scope: app.config.scope,
    }),
  steps: [
    {
      title: "Create a workload identity pool and provider",
      items: [
        {
          kind: "markdown",
          content:
            "Open the [Workload Identity Federation page](https://console.cloud.google.com/iam-admin/workload-identity-pools), click **Create pool**, and name the pool. Then add a provider of type **OpenID Connect (OIDC)**. For **Issuer (URL)**, enter:",
        },
        {
          kind: "copyable",
          label: "Issuer URL",
          value: "{OIDC_PROVIDER_URL}",
        },
        {
          kind: "markdown",
          content:
            "Skip the JWK file — Google fetches the keys from the issuer. Under **Audiences**, choose **Allowed audiences** and enter:",
        },
        {
          kind: "copyable",
          label: "Allowed audience",
          value: "connected-app:{CONNECTED_APP_ID}",
        },
        {
          kind: "markdown",
          content:
            "Under **Attribute mapping**, map **google.subject** to the value below, then create the provider:",
        },
        {
          kind: "copyable",
          label: "google.subject",
          value: "assertion.sub",
        },
      ],
    },
    {
      title: "Enter the provider details",
      items: [
        {
          kind: "input",
          label: "Project number",
          placeholder: "458876884405",
          required: true,
          target: { kind: "configuration", field: "projectNumber" },
          helperText:
            "The numeric ID in the pool page’s IAM principal (projects/<number>/…), also shown on the console’s project dashboard.",
        },
        {
          kind: "input",
          label: "Pool ID",
          placeholder: "my-pool",
          required: true,
          target: { kind: "configuration", field: "poolId" },
          helperText:
            "Open the pool’s details page and copy the value labeled “ID”. Google usually derives it from the pool name.",
        },
        {
          kind: "input",
          label: "Provider ID",
          placeholder: "3b",
          required: true,
          target: { kind: "configuration", field: "providerId" },
          helperText:
            "Open the provider’s details and copy the value labeled “ID”. Google usually derives it from the provider name.",
        },
      ],
    },
    {
      title: "Grant access to a service account (optional)",
      items: [
        {
          kind: "markdown",
          content:
            "By default, requests act as the federated identity itself — grant IAM roles directly to the pool principal and leave the fields below blank.\n\nTo act as a service account instead: create or pick a [service account](https://console.cloud.google.com/iam-admin/serviceaccounts) with the roles you need, then on the pool’s page click **Grant access**, choose **Grant access using service account impersonation**, select the service account, and under the principal filter choose the **subject** attribute with this value:",
        },
        {
          kind: "copyable",
          label: "Subject",
          value: "connected-app:{CONNECTED_APP_ID}",
        },
        {
          kind: "markdown",
          content:
            "Dismiss the “Configure your application” dialog Google offers afterwards — 3B doesn’t use a credential file. Then enter the service account’s email:",
        },
        {
          kind: "input",
          label: "Service account email (optional)",
          placeholder: "my-service-account@my-project.iam.gserviceaccount.com",
          target: { kind: "configuration", field: "serviceAccountEmail" },
          helperText:
            "Leave blank to use the federated identity directly, without impersonation.",
        },
        {
          kind: "input",
          label: "Scopes (optional)",
          placeholder: "https://www.googleapis.com/auth/cloud-platform",
          target: { kind: "configuration", field: "scope" },
          helperText:
            "Space-separated OAuth scopes for the access token. Defaults to https://www.googleapis.com/auth/cloud-platform.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: () => "https://sts.googleapis.com/.well-known/3b-connection-test",
  },
  usageHint: () =>
    "Requests to googleapis.com hosts are authenticated automatically with short-lived Google Cloud credentials. Make plain HTTPS calls to the APIs — no local credentials or GOOGLE_APPLICATION_CREDENTIALS needed.",
});

export const googleCloud = defineProduct({
  name: "Google Cloud",
  tagLine: "Google Cloud Platform services",
  logo,
  tags: ["featured", "it", "engineering"],
  variants: [serviceAccountKeyVariant, federationVariant],
});
